import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["viewMode", "editMode", "editButton", "cancelButton"];

  connect() {
    this.isEditing = false;

    // Use event delegation to catch form submits from dynamically added forms
    // Use capture phase to run before Turbo intercepts
    this.element.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (form && form.classList.contains("task-edit-form")) {
          this.syncTrixEditor(form);
        }
      },
      { capture: true },
    );

    // Auto-enter edit mode if ?edit=true is in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("edit") === "true") {
      setTimeout(() => this.enterEditMode(), 100);
    }
  }

  syncTrixEditor(form) {
    if (!form) return;

    // Search for Trix editor within the form
    const trixEditor = form.querySelector("trix-editor");
    if (!trixEditor || !trixEditor.editor) return;

    const inputId = trixEditor.getAttribute("input");
    if (!inputId) return;

    // Try to find the hidden input by ID first
    let hiddenInput = document.getElementById(inputId);

    // If not found by ID, try to find it within the form by name
    if (!hiddenInput) {
      hiddenInput = form.querySelector('input[name="task[description]"]');
    }

    if (!hiddenInput) return;

    // Get the HTML content from Trix editor
    // Since trixEditor.input might not be available, read from editor's innerHTML
    let trixContent = "";

    // Method 1: Try to read from Trix's input property (if available)
    if (trixEditor.input) {
      trixContent = trixEditor.input.value || "";
    } else {
      // Method 2: Read from editor's innerHTML directly
      // Trix maintains the HTML in the editor element
      const editorHTML = trixEditor.innerHTML;

      // The editor's HTML contains the content, but wrapped in Trix structure
      // Try to extract from .trix-content div, or use innerHTML directly
      const contentDiv = trixEditor.querySelector(".trix-content");
      if (contentDiv) {
        trixContent = contentDiv.innerHTML || "";
      } else {
        // Fallback: use the editor's innerHTML directly
        trixContent = editorHTML || "";
      }
    }

    // Ensure the hidden input has the current value
    if (trixContent) {
      hiddenInput.value = trixContent;
    }
  }

  toggleEdit(event) {
    event.preventDefault();

    if (this.isEditing) {
      this.exitEditMode();
    } else {
      this.enterEditMode();
    }
  }

  enterEditMode() {
    this.isEditing = true;

    // Hide view mode sections, show edit mode
    this.viewModeTargets.forEach((target) => {
      target.style.setProperty("display", "none", "important");
      target.style.setProperty("visibility", "hidden", "important");
    });

    this.editModeTargets.forEach((target) => {
      // Check parent elements and fix any that are hidden
      let parent = target.parentElement;
      let parentLevel = 0;
      while (parent && parentLevel < 5) {
        const computedStyle = window.getComputedStyle(parent);
        if (computedStyle.display === "none") {
          parent.style.setProperty("display", "block", "important");
        }
        parent = parent.parentElement;
        parentLevel++;
      }

      // Force display block and remove any inline styles that might hide it
      target.style.setProperty("display", "block", "important");
      target.style.setProperty("visibility", "visible", "important");
      target.style.setProperty("height", "auto", "important");
      target.style.setProperty("opacity", "1", "important");
      target.removeAttribute("hidden");
      target.classList.remove("hidden", "d-none");
    });

    // Update button text
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.innerHTML = '<i class="bi bi-x"></i> Cancel';
      this.editButtonTarget.classList.remove("btn-primary-custom");
      this.editButtonTarget.classList.add("btn-outline-custom");
    }

    // Initialize Trix editor with existing content
    // The form is initially hidden, so Trix might not have loaded the value
    // We need to manually load it when showing the form
    const initializeTrixEditor = () => {
      const editMode = this.element.querySelector(
        '[data-task-edit-target="editMode"]',
      );
      if (!editMode) return;

      const trixEditor = editMode.querySelector("trix-editor");
      if (!trixEditor) return;

      const inputId = trixEditor.getAttribute("input");
      if (!inputId) return;

      // Try to find the hidden input by ID first
      let hiddenInput = document.getElementById(inputId);

      // If not found by ID, try to find it within the form
      if (!hiddenInput) {
        const form = editMode.querySelector("form");
        if (form) {
          // Try to find by name attribute
          hiddenInput = form.querySelector('input[name="task[description]"]');
        }
      }

      if (!hiddenInput || !hiddenInput.value) return;

      // Function to load content into Trix
      const loadContent = () => {
        if (trixEditor.editor) {
          try {
            // Load the existing content into Trix editor
            trixEditor.editor.loadHTML(hiddenInput.value);
          } catch (error) {
            console.error("[TaskEdit] Error loading HTML into Trix:", error);
          }
        }
      };

      // Try to load immediately if Trix is ready
      if (trixEditor.editor) {
        loadContent();
      } else {
        // Trix not ready yet, wait for initialization
        const onInitialize = () => {
          loadContent();
          trixEditor.removeEventListener("trix-initialize", onInitialize);
        };
        trixEditor.addEventListener("trix-initialize", onInitialize, {
          once: true,
        });

        // Fallback: retry after a delay
        setTimeout(() => {
          if (trixEditor.editor && hiddenInput.value) {
            loadContent();
          }
        }, 300);
      }
    };

    // Wait a bit for the form to be visible, then initialize Trix
    setTimeout(initializeTrixEditor, 200);

    // Also try after a longer delay as fallback
    setTimeout(initializeTrixEditor, 500);

    // Focus first input in edit mode
    setTimeout(() => {
      const editMode = this.element.querySelector(
        '[data-task-edit-target="editMode"]',
      );
      if (editMode) {
        const firstInput = editMode.querySelector("input, textarea, select");
        if (firstInput && firstInput.type !== "hidden") {
          firstInput.focus();
          if (firstInput.select) {
            firstInput.select();
          }
        }
      }
    }, 250);
  }

  exitEditMode() {
    this.isEditing = false;

    // Show view mode, hide edit mode
    this.viewModeTargets.forEach((target) => {
      target.style.setProperty("display", "block", "important");
      target.style.setProperty("visibility", "visible", "important");
      target.removeAttribute("hidden");
      target.classList.remove("hidden", "d-none");
    });

    this.editModeTargets.forEach((target) => {
      target.style.setProperty("display", "none", "important");
      target.style.setProperty("visibility", "hidden", "important");
    });

    // Update button text
    if (this.hasEditButtonTarget) {
      this.editButtonTarget.innerHTML =
        '<i class="bi bi-pencil"></i> Edit Task';
      this.editButtonTarget.classList.remove("btn-outline-custom");
      this.editButtonTarget.classList.add("btn-primary-custom");
    }
  }

  cancel(event) {
    event.preventDefault();
    this.exitEditMode();

    // Reset form to original values
    const form = this.element.querySelector("form");
    if (form) {
      form.reset();
    }
  }

  handleFormSubmit(event) {
    // Also sync Trix when the Stimulus action is triggered (backup)
    this.syncTrixEditor(event.target);
  }
}
